import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { AButton, ATitle, Loading } from '../../../components';
import { Form, Row, Table } from 'antd';
import { useCallback, useEffect, useState, useMemo, useReducer } from 'react';
import {
  DislikeOutlined,
  FileProtectOutlined,
  LeftCircleOutlined,
  LikeOutlined,
} from '@ant-design/icons';
import {
  IDentalTreatment,
  IDentalTreatmentItem,
  IPaymentMethod,
} from '../../../shared/interfaces';
import { currency } from '../../../shared/utils/functions';
import RefuseTreatment from './Refuse';
import { getDentalTreatment, getSelectPaymentsMethods } from '../../../http';
import ConfirmDentalTreatment from './Confirm';
import {
  conditionConfig,
  PaintCondition,
} from '../../../components/odontogram/odontogram.types';
import { OdontogramRender } from '../../../components/odontogram/odontogram-render';
import {
  initialState,
  odontogramReducer,
} from '../../../components/odontogram/odontogram.reducer';
import { paintOdontogramFromData } from '../../../components/odontogram/odontogram.helpers';

const columns = [
  {
    title: '#',
    width: 75,
    render: (_: any, __: any, index: number) => <>{index + 1}</>,
  },
  {
    title: 'Dente',
    dataIndex: 'tooth_number',
    width: 100,
  },
  {
    title: 'Face',
    dataIndex: 'face',
    width: 100,
  },
  {
    title: 'Estado Atual',
    dataIndex: 'current_state',
    width: 180,
    render: (value: number) => {
      const nomeDaCondicao = conditionConfig[value as PaintCondition]?.nome;

      return (
        nomeDaCondicao ?? <span style={{ color: 'red' }}>ID Inválido</span>
      );
    },
  },
  {
    title: 'Procedimento',
    dataIndex: 'procedure_name',
    width: 200,
  },
  {
    title: 'Valor UN.',
    width: 130,
    dataIndex: 'value',
    render: (value: number) => <>{currency(value)}</>,
  },
  {
    title: 'Desconto',
    width: 130,
    render: (cell: IDentalTreatmentItem) => <>{currency(cell.discount)}</>,
  },
  {
    title: 'Total Final',
    width: 130,
    render: (cell: IDentalTreatmentItem) => (
      <>{currency(cell.value - cell.discount)}</>
    ),
  },
];

const DentalTreatmentApprovalOrNot: React.FC = () => {
  const { id } = useParams();
  const [odontogramState, dispatch] = useReducer(
    odontogramReducer,
    initialState,
  );
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [treatment, setTreatment] = useState<IDentalTreatment | null>(null);
  const [treatmentItems, setTreatmentItems] = useState<IDentalTreatmentItem[]>(
    [],
  );
  const [paymentsMethods, setPaymentsMethods] = useState<IPaymentMethod[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeModal, setActiveModal] = useState<'confirm' | 'refuse' | null>(
    null,
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [treatmentData, paymentsData] = await Promise.all([
        getDentalTreatment(id),
        getSelectPaymentsMethods(),
      ]);

      const { treatment: treatmentInfo, items } = treatmentData;

      if (items && items.length > 0) {
        paintOdontogramFromData(items, dispatch);
      }
      setTreatment(treatmentInfo);
      setTreatmentItems(items);
      setPaymentsMethods(paymentsData);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar dados',
        description:
          error?.response?.data?.message || 'Tente novamente mais tarde.',
      });
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  const renderModalConfirm = () => {
    if (activeModal === 'confirm') {
      return (
        <ConfirmDentalTreatment
          open={activeModal === 'confirm'}
          close={() => setActiveModal(null)}
          dentalTreatment={treatment}
          paymentsMethods={paymentsMethods}
          setApproval={null}
        />
      );
    }
  };

  const renderModalRefuse = () => {
    if (activeModal === 'refuse') {
      return (
        <RefuseTreatment
          open={activeModal === 'refuse'}
          close={() => setActiveModal(null)}
          treatment={treatment}
        />
      );
    }
  };

  const renderButtonsApprovalRefuse = () => {
    if (!treatment?.approval) {
      return (
        <>
          <AButton
            type="primary"
            danger
            onClick={() => setActiveModal('refuse')}
            icon={<DislikeOutlined />}
          >
            Recusar
          </AButton>
          <AButton
            type="primary"
            onClick={() => setActiveModal('confirm')}
            icon={<LikeOutlined />}
          >
            Aprovar
          </AButton>
        </>
      );
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalValue = useMemo(() => {
    const total = treatmentItems.reduce(
      (acc, current) => acc + (current.value - current.discount),
      0,
    );
    return total;
  }, [treatmentItems]);

  const pageTitle = `${treatment?.type === 1 ? 'Tratamento' : 'Orçamento'} Nº ${
    treatment?.code || ''
  }`;

  if (loading) return <Loading load />;

  return (
    <>
      <ATitle icon={<FileProtectOutlined />}>{pageTitle}</ATitle>
      <Form layout="vertical" style={{ width: '100%', marginTop: 20 }}>
        <Row justify="end" style={{ gap: '8px' }}>
          <AButton
            type="default"
            onClick={() => navigate(-1)}
            icon={<LeftCircleOutlined />}
          >
            Voltar
          </AButton>
          {renderButtonsApprovalRefuse()}
        </Row>
      </Form>
      <OdontogramRender
        odontogramState={odontogramState}
        dispatch={dispatch}
        condicaoSelecionada={null}
      />
      <Table
        style={{ width: '100%', margin: '20px 0px' }}
        scroll={{ y: 350 }}
        columns={columns}
        dataSource={treatmentItems}
        pagination={false}
        bordered
        title={() => <ATitle>Procedimentos inclusos</ATitle>}
        footer={() => (
          <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
            <ATitle>Total</ATitle>
            <ATitle>{currency(totalValue)}</ATitle>
          </Row>
        )}
      />
      {renderModalConfirm()}
      {renderModalRefuse()}
    </>
  );
};

export default DentalTreatmentApprovalOrNot;
